import { TypedDocumentNode, gql } from '@apollo/client';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { FOOTBALL_PLAY_RIVALS } from '@sorare/core/src/constants/__generated__/routes';
import { isType } from '@sorare/core/src/gql';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { PrimaryBuyGrid } from '@sorare/marketplace/src/components/primaryBuy/PrimaryBuyGrid';

import {
  MarketplacePlayerDrawer,
  Props as MarketplacePlayerDrawerProps,
} from '@sorare/gameplay/src/components/player/MarketplacePlayerDrawer';

import { GameHeader } from 'components/rivals/GameHeader';

import {
  RivalsMatchMarketQuery,
  RivalsMatchMarketQueryVariables,
} from './__generated__/page.graphql';

const RIVALS_MATCH_MARKET_QUERY = gql`
  query RivalsMatchMarketQuery($slug: String!) {
    football {
      rivals {
        id
        game(slug: $slug) {
          slug
          game {
            id
            homeTeam {
              slug
              ...PrimaryBuyGrid_teamInterface
            }
            awayTeam {
              slug
              ...PrimaryBuyGrid_teamInterface
            }
            ...GameHeader_game
            homeFormation {
              startingLineup {
                id
                slug
              }
            }
            awayFormation {
              startingLineup {
                id
                slug
              }
            }
          }
        }
        currentManager {
          id
        }
      }
    }
  }
  ${GameHeader.fragments.game}
  ${PrimaryBuyGrid.fragments.teamInterface}
` as TypedDocumentNode<RivalsMatchMarketQuery, RivalsMatchMarketQueryVariables>;

const RivalsInstantBuy = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useQuery(RIVALS_MATCH_MARKET_QUERY, {
    variables: { slug },
    skip: !slug,
    fetchPolicy: 'cache-and-network',
  });
  const { game } = data?.football.rivals.game || {};
  const { homeTeam, awayTeam, awayFormation, homeFormation } = game || {};
  const starting22 = [
    ...(homeFormation?.startingLineup || []),
    ...(awayFormation?.startingLineup || []),
  ]
    .flat()
    .map(p => p.slug);
  const [cardOpened, setCardOpened] =
    useState<MarketplacePlayerDrawerProps['cardOpened']>(null);

  if (loading) return null;
  if (!slug || !game) return <Navigate to={FOOTBALL_PLAY_RIVALS} />;
  return (
    <>
      <PageHeader>
        <Horizontal gap={1}>
          <IconButton
            color="transparent"
            onClick={() => {
              navigate(-1);
            }}
            icon={faChevronLeft}
          />
          <GameHeader game={game} />
        </Horizontal>
      </PageHeader>
      <PrimaryBuyGrid
        homeTeam={homeTeam}
        awayTeam={awayTeam}
        gameSlug={slug}
        isNationalTeam={isType(homeTeam, 'NationalTeam')}
        onPrimaryBuySuccess={() => navigate('/')}
        onInfoClick={(cardSlug, playerSlug) => {
          setCardOpened({ cardSlug, playerSlug });
        }}
        startingLineup={starting22}
      />
      <MarketplacePlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
      />
    </>
  );
};

export default RivalsInstantBuy;
